import { computed } from 'vue';
import { postureDiffersFromOriginal } from '@/domain/anatomy/hipSpineLandmarks';
import type { HipSpineXRayModalContext } from '@/views/3d-planner/hipSpine/xrayModal/modalState';

/**
 * Reset-side gating: whether a "Reset to AI" is offered, and the tab-disabled
 * predicate. Reset and Approve both mutate the active posture's server state, so
 * each gate reads the other's in-flight flag to serialise the two.
 */
export function createModalResetGating(ctx: HipSpineXRayModalContext) {
    const { store, sync, isResetting, isApproving, activePosture, activeViewState } = ctx;

    function isTabDisabled(value: string): boolean {
        return !store.hipSpinePostures.some((posture) => posture.posture === value);
    }

    const canReset = computed(() => {
        const posture = activePosture.value;
        const viewState = activeViewState.value;
        if (!posture || !viewState) return false;
        return postureDiffersFromOriginal(posture, viewState.landmarks);
    });

    // Reset is unavailable when there's nothing to reset, a reset is already running, an
    // approve is in flight, or a save is in flight. Gating on `isApproving` mirrors
    // `isApproveDisabled`'s `isResetting` gate: Approve snapshots the server landmarks
    // while Reset re-POSTs and re-fetches them, so letting both run at once would race
    // over the same posture's state. `sync.isSaving` likewise blocks racing an auto-save.
    const isResetDisabled = computed(
        () => !canReset.value || isResetting.value || isApproving.value || sync.isSaving,
    );

    return { isTabDisabled, canReset, isResetDisabled };
}

/**
 * Approve-side gating: load-error flags, current approval state, and whether the
 * Approve button is offered. Kept in the ViewModel (not the template) so the
 * View stays presentation-only.
 */
export function createModalApproveGating(ctx: HipSpineXRayModalContext) {
    const { store, sync, savePlan, isApproving, isResetting, approveSuccess, activeViewState } = ctx;

    const hasImageError = computed(() => activeViewState.value?.imageSize === null);
    const hasLandmarkError = computed(() => activeViewState.value?.landmarks.length === 0);

    const isCurrentPosturePending = computed(() =>
        store.activePostureId ? store.isPosturePending(store.activePostureId) : false,
    );

    // Annotation visibility toggles are presentation-only and never gate
    // approval — only load failures (image or landmarks) block it.
    const canApprove = computed(() => !hasImageError.value && !hasLandmarkError.value);

    // The active posture's annotations are approved — just succeeded, or already
    // approved (not pending). Semantic state the View maps to the label + check icon.
    const isActivePostureApproved = computed(
        () => approveSuccess.value || !isCurrentPosturePending.value,
    );

    // The active posture has unsaved edits (queued or in-flight). Approve snapshots the
    // server's current landmarks, so approving while dirty would capture pre-edit state —
    // including during the pre-save debounce window when `sync.isSaving` is still false.
    const isActivePostureDirty = computed(() =>
        store.activePostureId ? savePlan.isPostureDirty(store.activePostureId) : false,
    );

    // Approve is available only for a pending posture that has no blocking errors, is not
    // mid-approve, mid-reset, or mid-save, and has no unsaved edits.
    const isApproveDisabled = computed(
        () =>
            !canApprove.value ||
            isApproving.value ||
            isResetting.value ||
            !isCurrentPosturePending.value ||
            sync.isSaving ||
            isActivePostureDirty.value,
    );

    return {
        hasImageError,
        hasLandmarkError,
        isCurrentPosturePending,
        canApprove,
        isActivePostureApproved,
        isActivePostureDirty,
        isApproveDisabled,
    };
}
